import React from 'react';

import { flush } from '@amplitude/analytics-browser';
import { css } from '@emotion/react';
import { NeutralColorToken, TextButton, Type } from '@robinhood/design';

import UserCreationDisclosure from './UserCreationDisclosure';
import logger from 'src/clients/LoggingClient';
import { ApiEnv, Domain, OauthClientId, OauthScope, fetch } from 'src/clients/Network/http/fetch';
import { Event, track } from 'src/clients/tracking';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import IconButton from 'src/components/creditApplication/IconButton';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import TextInput from 'src/components/creditApplication/TextInput';
import { LoginPageUrl } from 'src/constants/urls';
import { RECAPTCHA_API_KEY, executeRecaptcha, useRecaptcha } from 'src/hooks/useRecaptcha';
import { SheriffTokenResponse, setAuthState } from 'src/state/auth/auth';
import { showGlobalErrorAlert, useGlobalStore } from 'src/state/global';
import { getDeviceId } from 'src/utils/deviceId';
import { isValidEmail } from 'src/utils/validations';

const UserCreationPage = () => {
  useRecaptcha();
  const { nextPage, nextPageLoading } = React.useContext(NavigationContext);
  const [firstName, setFirstName] = React.useState<string>('');
  const [lastName, setLastName] = React.useState<string>('');
  const [email, setEmail] = React.useState<string>('');
  const [password, setPassword] = React.useState<string>('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [emailError, setEmailError] = React.useState<string>();
  const [passwordError, setPasswordError] = React.useState<string>();
  const buttonState = React.useMemo(() => {
    return loading || nextPageLoading
      ? 'loading'
      : isValidEmail(email) &&
          firstName.length >= 1 &&
          lastName.length >= 1 &&
          password.length >= 1 &&
          !passwordError &&
          !emailError
        ? 'default'
        : 'disabled';
  }, [password, email, firstName, lastName, loading, nextPageLoading, passwordError, password, emailError]);

  const onChangeFirstName = React.useCallback(({ value }: { value: string }) => {
    setFirstName(value);
  }, []);

  const onChangeLastName = React.useCallback(({ value }: { value: string }) => {
    setLastName(value);
  }, []);

  const onChangeEmail = React.useCallback(({ value }: { value: string }) => {
    setEmailError('');
    setEmail(value);
  }, []);

  const onChangePassword = React.useCallback(({ value }: { value: string }) => {
    setPassword(value);
    setPasswordError('');
  }, []);

  const togglePasswordVisibility = React.useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const onClickLogin = React.useCallback(() => {
    track(Event.RhWebCreditApplicationExistingUserLogin);
    flush();
  }, []);

  const onSubmit = React.useCallback(async () => {
    if (password.length < 10) {
      setPasswordError('Your password must be at least 10 characters.');
      return;
    }
    setLoading(true);
    try {
      const signupGrToken = await executeRecaptcha('signup');
      if (!signupGrToken) {
        showGlobalErrorAlert('signup-recaptcha-failed');
        return;
      }
      const signupRes = await fetch(Domain.RHPublic, `/user/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          username: email,
          password,
          first_name: firstName,
          last_name: lastName,
          gr_key: RECAPTCHA_API_KEY,
          gr_token: signupGrToken,
          origin: { locality: 'US' },
        }),
      });
      if (!signupRes.ok) {
        if (signupRes.status === 400) {
          const body = await signupRes.json();
          if ('email' in body && body.email.length > 0) {
            setEmailError(body.email.at(0));
            return;
          }
          if ('password' in body && body.password.length > 0) {
            setPasswordError(body.password.at(0));
            return;
          }
        }
        showGlobalErrorAlert('user-creation-failed');
        return;
      }

      track(Event.RhWebCreditApplicationNewUserSheriffSignup);

      const loginGrToken = await executeRecaptcha('login');
      if (!loginGrToken) {
        showGlobalErrorAlert('login-recaptcha-failed');
        return;
      }
      const loginRes = await fetch(Domain.RHPublic, `/oauth2/token/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: email,
          password,
          gr_key: RECAPTCHA_API_KEY,
          gr_token: loginGrToken,
          create_read_only_secondary_access_token: true,
          expires_in: 86400,
          grant_type: 'password',
          scope: OauthScope,
          token_request_path: '/creditcard/application',
          client_id: ApiEnv === 'production' ? OauthClientId.Production : OauthClientId.Apollo,
          device_token: getDeviceId(),
        }),
      });
      if (loginRes.ok) {
        const body = await loginRes.json();
        if ('access_token' in body) {
          setAuthState(body as SheriffTokenResponse);
          useGlobalStore.setState({
            personalInformation: {
              firstName,
              lastName,
              email,
            },
          });
          nextPage();
        } else {
          showGlobalErrorAlert('login-missing-access-token');
        }
      } else {
        showGlobalErrorAlert('login-failed');
      }
    } catch (e) {
      logger.error(e, { source: 'UserCreationPage.onSubmit' });
      showGlobalErrorAlert('unexpected-exception');
    } finally {
      setLoading(false);
    }
  }, [email, firstName, lastName, password]);

  return (
    <CreditApplicationPageLayout
      title='Your information'
      subtitle={
        <span>
          Enter your information as it appears on your I.D.
          <br />
          <Type.Text size='M' color={NeutralColorToken.Fg2}>
            Already a Robinhood customer?{' '}
            <TextButton
              label='Log in'
              color={NeutralColorToken.Fg2}
              variant='inline'
              type='anchor'
              href={LoginPageUrl}
              onClick={onClickLogin}
            />
          </Type.Text>
        </span>
      }
    >
      <div css={root}>
        <TextInput
          placeholder='Legal first name'
          value={firstName}
          onChange={onChangeFirstName}
          autoComplete='given-name'
        />
        <TextInput
          placeholder='Legal last name'
          value={lastName}
          onChange={onChangeLastName}
          autoComplete='family-name'
        />
        <TextInput
          placeholder='Email'
          state={!!emailError ? 'error' : 'default'}
          error={emailError}
          value={email}
          onChange={onChangeEmail}
          type='email'
          autoComplete='email'
        />
        <TextInput
          placeholder='Set a password'
          state={!!passwordError ? 'error' : 'default'}
          error={passwordError}
          value={password}
          onChange={onChangePassword}
          type={showPassword ? 'text' : 'password'}
          autoComplete='new-password'
          endControl={
            <IconButton
              type='button'
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              icon={showPassword ? 'EyeClosed16' : 'Eye16'}
              onClick={togglePasswordVisibility}
            />
          }
        />
        <UserCreationDisclosure />
        <div css={footer}>
          <Button label='Continue' state={buttonState} width='full' type='submit' onClick={onSubmit} />
        </div>
      </div>
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const footer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-top: 24px;
  text-align: center;
`;

export default UserCreationPage;
